import { observable, runInAction } from "mobx";
import { ColLineModel, IChartServices, NowPreviousModel, TotalCustomerModel, ValueCustomerModel } from "services/Main/chart_services";
import { IHomeServices, ValueModel, ValueNewsModel, ValueTaskModel } from "services/Main/home_services";
import { DataResponse } from "services/response";

export type HomeStore = {
    // Lấy data bảng thông tin - tổng khách hàng
    listCountCustomerResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng thông tin - tổng liên hệ
    listCountContactResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng thông tin - tổng công việc
    listCountTaskResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng thông tin - công việc được phân công
    listCountTaskToDoResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng thông tin - công việc đang thực hiện
    listCountTaskProcessingResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng thông tin - công việc đã quá hạn
    listCountTaskExpiredResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng thông tin - công việc đã hoàn thành
    listCountTaskCompletedResponse: DataResponse<Array<ValueModel>>;
    // Lấy data cập nhật hoạt động - số lượng ghé thăm
    listCountVisitingResponse: DataResponse<Array<ValueModel>>;
    // Lấy data cập nhật hoạt động - thăm hỏi khách hàng
    listCountVisitingCustomerResponse: DataResponse<Array<ValueModel>>;
    // Lấy data cập nhật hoạt động - nhiệm vụ khác
    listCountActivitiesResponse: DataResponse<Array<ValueModel>>;
    // Lấy data bảng tin
    listNewsViewResponse: DataResponse<Array<ValueNewsModel>>;
    // Lấy data thông tin cập nhật
    listUpdateInformationResponse: DataResponse<ValueTaskModel | undefined>;
}

export interface IHomePresenter {
    createStore(): HomeStore;
    // Lấy data bảng thông tin - tổng khách hàng
    getListCountCustomer(store: HomeStore): Promise<void>;
    // Lấy data bảng thông tin - tổng liên hệ
    getListCountContact(store: HomeStore): Promise<void>;
    // Lấy data bảng thông tin - tổng công việc
    getListCountTask(store: HomeStore): Promise<void>;
    // Lấy data bảng thông tin - công việc được phân công
    getListCountTaskToDo(store: HomeStore, employeeCode: string): Promise<void>;
    // Lấy data bảng thông tin - công việc đang thực hiện
    getListCountTaskProcessing(store: HomeStore, employeeCode: string): Promise<void>;
    // Lấy data bảng thông tin - công việc đã quá hạn
    getListCountTaskExpired(store: HomeStore, employeeCode: string): Promise<void>;
    // Lấy data bảng thông tin - công việc đã quá hạn
    getListCountTaskCompleted(store: HomeStore, employeeCode: string): Promise<void>;
    // Lấy data cập nhật hoạt động - số lượng ghé thăm
    getListCountVisiting(store: HomeStore, selectedCommonDate: string): Promise<void>;
    // Lấy data cập nhật hoạt động - thăm hỏi khách hàng
    getListCountVisitingCustomer(store: HomeStore, selectedCommonDate: string): Promise<void>;
    // Lấy data cập nhật hoạt động - nhiệm vụ khác
    getListCountActivities(store: HomeStore, selectedCommonDate: string): Promise<void>;
    // Lấy data bảng tin
    getListNewsView(store: HomeStore, accountId: string): Promise<void>;
    // Lấy data thông tin cập nhật
    getListUpdateInformation(store: HomeStore): Promise<void>;

}

export function createHomePresenter({ homeServices }: { homeServices: IHomeServices }): IHomePresenter {
    return {
        createStore: (): HomeStore =>
            observable({
                // Lấy data bảng thông tin - tổng khách hàng
                listCountCustomerResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng thông tin - tổng liên hệ
                listCountContactResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng thông tin - tổng công việc
                listCountTaskResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng thông tin - công việc được phân công
                listCountTaskToDoResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng thông tin - công việc đang thực hiện
                listCountTaskProcessingResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng thông tin - công việc đã quá hạn
                listCountTaskExpiredResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng thông tin - công việc đã quá hạn
                listCountTaskCompletedResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data cập nhật hoạt động - số lượng ghé thăm
                listCountVisitingResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data cập nhật hoạt động - thăm hỏi khách hàng
                listCountVisitingCustomerResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data cập nhật hoạt động - nhiệm vụ khác
                listCountActivitiesResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data bảng tin
                listNewsViewResponse: {
                    data: [],
                    message: '',
                    isSuccess: false,
                },
                // Lấy data thông tin cập nhật
                listUpdateInformationResponse: {
                    data: undefined,
                    message: '',
                    isSuccess: false,
                }
            }),

        // Hàm lấy data bảng thông tin - tổng khách hàng
        getListCountCustomer: async (store) => {
            try {
                const result = await homeServices.getListCountCustomer();
                runInAction(() => {
                    store.listCountCustomerResponse.data = result.data;
                    store.listCountCustomerResponse.message = result.message;
                    store.listCountCustomerResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng thông tin - tổng liên hệ
        getListCountContact: async (store) => {
            try {
                const result = await homeServices.getListCountContact();
                runInAction(() => {
                    store.listCountContactResponse.data = result.data;
                    store.listCountContactResponse.message = result.message;
                    store.listCountContactResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng thông tin - tổng công việc
        getListCountTask: async (store) => {
            try {
                const result = await homeServices.getListCountTask();
                runInAction(() => {
                    store.listCountTaskResponse.data = result.data;
                    store.listCountTaskResponse.message = result.message;
                    store.listCountTaskResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng thông tin - công việc được phân công
        getListCountTaskToDo: async (store, employeeCode) => {
            try {
                const result = await homeServices.getListCountTaskToDo(employeeCode);
                runInAction(() => {
                    store.listCountTaskToDoResponse.data = result.data;
                    store.listCountTaskToDoResponse.message = result.message;
                    store.listCountTaskToDoResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng thông tin - công việc đang thực hiện
        getListCountTaskProcessing: async (store, employeeCode) => {
            try {
                const result = await homeServices.getListCountTaskProcessing(employeeCode);
                runInAction(() => {
                    store.listCountTaskProcessingResponse.data = result.data;
                    store.listCountTaskProcessingResponse.message = result.message;
                    store.listCountTaskProcessingResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng thông tin - công việc đã quá hạn
        getListCountTaskExpired: async (store, employeeCode) => {
            try {
                const result = await homeServices.getListCountTaskExpired(employeeCode);
                runInAction(() => {
                    store.listCountTaskExpiredResponse.data = result.data;
                    store.listCountTaskExpiredResponse.message = result.message;
                    store.listCountTaskExpiredResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng thông tin - công việc đã quá hạn
        getListCountTaskCompleted: async (store, employeeCode) => {
            try {
                const result = await homeServices.getListCountTaskCompleted(employeeCode);
                runInAction(() => {
                    store.listCountTaskCompletedResponse.data = result.data;
                    store.listCountTaskCompletedResponse.message = result.message;
                    store.listCountTaskCompletedResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data cập nhật hoạt động - số lượng ghé thăm
        getListCountVisiting: async (store, selectedCommonDate) => {
            try {
                const result = await homeServices.getListCountVisiting(selectedCommonDate);
                runInAction(() => {
                    store.listCountVisitingResponse.data = result.data;
                    store.listCountVisitingResponse.message = result.message;
                    store.listCountVisitingResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data cập nhật hoạt động - thăm hỏi khách hàng
        getListCountVisitingCustomer: async (store, selectedCommonDate) => {
            try {
                const result = await homeServices.getListCountVisitingCustomer(selectedCommonDate);
                runInAction(() => {
                    store.listCountVisitingCustomerResponse.data = result.data;
                    store.listCountVisitingCustomerResponse.message = result.message;
                    store.listCountVisitingCustomerResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data cập nhật hoạt động - nhiệm vụ khác
        getListCountActivities: async (store, selectedCommonDate) => {
            try {
                const result = await homeServices.getListCountActivities(selectedCommonDate);
                runInAction(() => {
                    store.listCountActivitiesResponse.data = result.data;
                    store.listCountActivitiesResponse.message = result.message;
                    store.listCountActivitiesResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data bảng tin
        getListNewsView: async (store, accountId) => {
            try {
                const result = await homeServices.getListNewsView(accountId);
                runInAction(() => {
                    store.listNewsViewResponse.data = result.data;
                    store.listNewsViewResponse.message = result.message;
                    store.listNewsViewResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
        // Hàm lấy data thông tin cập nhật
        getListUpdateInformation: async (store) => {
            try {
                const result = await homeServices.getListUpdateInformation();
                runInAction(() => {
                    store.listUpdateInformationResponse.data = result.data;
                    store.listUpdateInformationResponse.message = result.message;
                    store.listUpdateInformationResponse.isSuccess = result.isSuccess;
                });
            } catch (error) {
                console.log(error);
            }
        },
    }
}